import { Icon } from "@deligoo/ui"
import { IconName } from "@deligoo/ui/src/icons/build"
import clsx from "clsx"
import { useCallback, useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { batch, shallowEqual, useDispatch } from "react-redux"

import { MapContext, useAppSelector } from "@/App"
import { Main } from "@/components/AppLayout"
import { selectCount } from "@/store"
import { selectActiveDriversIds } from "@/store/drivers"
import { selectFilteredOrdersIds } from "@/store/orders"
import {
  retrieveSavedMapViewport,
  saveMapViewport,
  setAboutPanel,
  setFocusedDriver,
  setFocusedOrder,
  setFocusedOrderArea,
  setPanelHidden,
  setShowDriverDetails,
  setShowOrderDetails,
} from "@/store/ui"
import { Driver, Order, OrderPoint } from "@/types"
import { OrderUtils } from "@/utils"

import cls from "./MapView.module.scss"
import { About } from "./partials/About"
import { DriversPanel } from "./partials/Drivers/DriversPanel"
import { MainMap } from "./partials/Map"
import { MapSearch } from "./partials/Map/MapSearch"
import { MapLayers } from "./partials/MapLayers"
import { MapTeamStats } from "./partials/MapTeamStats"
import { OrdersPanel } from "./partials/Orders/OrdersPanel"

export type MapRoute = {
  from: [number, number]
  to: [number, number]
  color: Order["state"] | OrderPoint["state"]
  transport?: Driver["transport_type"]
}

const filterAssignedAndUnassigned = (order: Order) => OrderUtils.isUnassigned(order) || OrderUtils.isAssigned(order)

export const MapView = () => {
  const { t } = useTranslation()

  const dispatch = useDispatch()

  const panelHidden = useAppSelector((state) => state.ui.panelHidden, shallowEqual)
  const aboutPanel = useAppSelector((state) => state.ui.aboutPanel, shallowEqual)

  const showOrderDetails = useAppSelector((state) => state.ui.showOrderDetails)
  const showDriverDetails = useAppSelector((state) => state.ui.showDriverDetails)

  const activeDrivers = useAppSelector(selectActiveDriversIds, shallowEqual)
  const ordersAssignedAndUnassigned = useAppSelector(selectCount(selectFilteredOrdersIds(filterAssignedAndUnassigned)))

  const [routes, _setRoutes] = useState<MapRoute[]>([])
  const setRoutes = useCallback(_setRoutes, [_setRoutes])

  const togglePanel = useCallback(
    (side: "left" | "right") => dispatch(setPanelHidden({ ...panelHidden, [side]: !panelHidden[side] })),
    [dispatch, panelHidden]
  )

  const [mobileTab, setMobileTab] = useState("ordersTab")

  const mapRef = useContext(MapContext)

  useMapViewportSavedOnUnmount()

  function handleMobileTabClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    setMobileTab(event.currentTarget.value)
  }

  useEffect(() => {
    const currentTab = showDriverDetails ? "couriersTab" : "ordersTab"

    setMobileTab(currentTab)
  }, [showDriverDetails, showOrderDetails])

  return (
    <Main overflowHidden="all" wrapperClassName={cls.wrapper}>
      <nav className={cls.mobileTabs}>
        <MapTeamStats displayOnlyOnMobile={true} />

        <button
          onClick={handleMobileTabClick}
          value="ordersTab"
          className={clsx(cls.tab, mobileTab === "ordersTab" && cls.active)}
          type="button"
        >
          {t("orders")} ({ordersAssignedAndUnassigned || 0})
        </button>

        <button
          onClick={handleMobileTabClick}
          value="couriersTab"
          className={clsx(cls.tab, mobileTab === "couriersTab" && cls.active)}
          type="button"
        >
          {t("couriers")} ({activeDrivers.length || 0})
        </button>
      </nav>

      <div
        className={clsx(
          cls.mapView,
          mobileTab && cls[mobileTab],
          (showDriverDetails || showOrderDetails) && cls.details
        )}
        onTransitionEnd={() => mapRef?.current?.leafletElement.invalidateSize()}
      >
        <OrdersPanel className={clsx(cls.orders, panelHidden.left && cls.isHidden)} />

        <section className={cls.main}>
          <MainMap routes={routes} className={cls.map} />

          <div className={cls.controls}>
            <div className={cls.nav}>
              <div>
                <PanelToggler side="left" active={panelHidden.left} onClick={() => togglePanel("left")} />
                <MapLayers />
              </div>

              <div>
                <MapTeamStats />
                <PanelToggler side="right" active={panelHidden.right} onClick={() => togglePanel("right")} />
              </div>
            </div>

            <MapSearch />

            {aboutPanel && (
              <About
                type={aboutPanel.type}
                id={aboutPanel.id}
                close={() => {
                  batch(() => {
                    dispatch(setAboutPanel(false))
                    dispatch(setShowDriverDetails(false))
                    dispatch(setShowOrderDetails(false))
                    dispatch(setFocusedDriver(null))
                    dispatch(setFocusedOrder(null))
                    dispatch(setFocusedOrderArea(null))
                  })
                }}
                setRoutes={setRoutes}
              />
            )}
          </div>
        </section>

        <DriversPanel className={clsx(cls.drivers, panelHidden.right && cls.isHidden)} />
      </div>
    </Main>
  )
}

type PanelTogglerProps = {
  active: boolean
  side: "left" | "right"
  onClick: () => void
}

const PanelToggler = ({ active, side, onClick }: PanelTogglerProps) => {
  return (
    <button className={clsx(cls.panelToggler, active && cls.active)} onClick={onClick}>
      <Icon name={`chevron-tiny-bold-${side}` as IconName} className={cls.icon} />
    </button>
  )
}

function useMapViewportSavedOnUnmount() {
  const dispatch = useDispatch()

  const mapRef = useContext(MapContext)

  useEffect(() => {
    const map = mapRef?.current

    return () => {
      dispatch(saveMapViewport(map?.viewport || null))
    }
  }, [dispatch, mapRef])

  useEffect(() => {
    const map = mapRef?.current

    dispatch(retrieveSavedMapViewport(map))
  }, [dispatch, mapRef])
}

export function useMapViewportSavedOnMount() {
  const dispatch = useDispatch()

  const mapRef = useContext(MapContext)

  useEffect(() => {
    const map = mapRef?.current

    dispatch(saveMapViewport(map?.viewport || null))

    return () => {
      dispatch(retrieveSavedMapViewport(map))
    }
  }, [dispatch, mapRef])
}
